import React, { useContext, useEffect, useState } from 'react';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Tooltip from '@amzn/meridian/tooltip';
import Box from '@amzn/meridian/box';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Icon from '@amzn/meridian/icon';
import { getStationCode } from '../../utils/networkUtil';
import { GlobalStateContext } from '../../global/context/GlobalStateContext';
import apis from '../../utils/apis';
import { Column } from '@amzn/meridian';

const TotalVolumeView = ({ selectedCycle }) => {
  const { setAsyncError } = useContext(GlobalStateContext);
  const [pendingInduct, setPendingInduct] = useState(0);
  const [pendingStow, setPendingStow] = useState(0);
  const [pendingDepart, setPendingDepart] = useState(0);

  const extractMetrics = (dwellPackageData) => {
    const calculateMetricForStatusConfigs = (statusConfigs, directionKey = 'direction') => {
      let total = 0;
      if (dwellPackageData?.metricResult && selectedCycle) {
        dwellPackageData.metricResult.forEach((item) => {
          const { packageStatus, columnToViewDataMap, metaDataMap } = item;
          // Find matching status config
          const matchingConfig = statusConfigs.find(
            (config) =>
              config.status === packageStatus &&
              // Check within the metaDataMap to filter the processName with direction
              (!config.directionFilter || (metaDataMap && metaDataMap[directionKey] === config.directionFilter))
          );
          if (columnToViewDataMap && matchingConfig) {
            Object.entries(columnToViewDataMap).forEach(([column, data]) => {
              if (column.includes(selectedCycle) && data.value) {
                total += Number(data.value) || 0;
              }
            });
          }
        });
      }
      return total;
    };

    const pendingInductConfigs = [
      { status: 'Arrived' },
      { status: 'Received' },
      { status: 'Held On Ead' },
      { status: 'Marked for Reprocess' },
      { status: 'Induct Problem Solve' },
      { status: 'Inducted', directionFilter: 'DS/Store -> Customer' }
    ];

    const pendingStowConfigs = [
      { status: 'Inducted' },
      { status: 'Stow Problem Solve' },
      { status: 'Stow Buffered' },
      { status: 'Jackpot Buffered' }
    ];

    const pendingDepartConfigs = [
      { status: 'Stowed' },
      { status: 'Staged', directionFilter: 'DS -> Customer' },
      { status: 'Picked' },
      { status: 'Pick Problem Solve' }
    ];

    // Calculate metrics
    const pendingInduct = calculateMetricForStatusConfigs(pendingInductConfigs);
    const pendingStow = calculateMetricForStatusConfigs(pendingStowConfigs);
    const pendingDepart = calculateMetricForStatusConfigs(pendingDepartConfigs);

    setPendingInduct(pendingInduct);
    setPendingStow(pendingStow);
    setPendingDepart(pendingDepart);
  };

  // Call oculus to get DWELL metrics
  const callOculus = async () => {
    return await apis['GET_DWELL_METRICS'](
      {
        query: {
          nodeId: getStationCode(),
          view: 'DWELLING_VIEW',
          filters: [
            {
              __type: 'TermFilter:http://internal.amazon.com/coral/com.amazon.oculusservice.model.filter/',
              filterMap: { SHIPMENT_TYPE: ['Delivery'] }
            },
            {
              __type: 'RangeFilter:http://internal.amazon.com/coral/com.amazon.oculusservice.model.filter/',
              filterMap: {}
            }
          ],
          leg: 'FORWARD'
        }
      },
      // Pass empty function to silently handle errors
      () => {}
    );
  };

  // Fetch dwell package data when component mounts or selectedCycle changes
  useEffect(() => {
    const fetchAndProcess = async () => {
      try {
        const dwellMetrics = await callOculus();
        extractMetrics(dwellMetrics);
      } catch (error) {
        // Silently handle any exceptions
      }
    };

    fetchAndProcess();
  }, [selectedCycle]);

  return (
    <Box type="outline" spacingInset="400">
      <Heading level={2} type="h300" alignment="left" className="overview-fph">
        Volume
        <Tooltip position="end" title="for the selected cycle">
          <Icon className={'custom-tool-tip'} tokens={infoKnockoutTokens} />
        </Tooltip>
      </Heading>
      <Row width="100%" alignmentVertical="top">
        <Column spacing="50" alignmentVertical="center">
          <Text type="d50">{pendingInduct}</Text>
          <Text type="b100">Pending Induct</Text>
        </Column>
        <Column spacing="50" alignmentVertical="center">
          <Text type="d50">{pendingStow}</Text>
          <Text type="b100">Pending Stow</Text>
        </Column>
        <Column spacing="50" alignmentVertical="center">
          <Text type="d50">{pendingDepart}</Text>
          <Text type="b100">Pending Depart</Text>
        </Column>
      </Row>
    </Box>
  );
};

export default TotalVolumeView;
